'use client'

import { useState, useEffect, useRef, useId, type FormEvent } from 'react'
import { useTheme } from './ThemeProvider'
import { useLocale } from './LocaleProvider'
import { useLoader } from './LoaderContext'
import RecaptchaLoader from './RecaptchaLoader'
import LanguageSelect from './LanguageSelect'
import { AnimatedPillButton } from './AnimatedPill'

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

const navItemsKeys = [
  { key: 'nav.about', href: '#about' },
  { key: 'nav.projects', href: '#projects' },
  { key: 'nav.experience', href: '#experience' },
  { key: 'nav.explorations', href: '#notes' },
  { key: 'nav.designArchive', href: '#design-archive' },
] as const

const socialLinks = [
  { 
    name: 'LinkedIn', 
    href: 'https://www.linkedin.com/in/damenoosthuizen/',
    iconPath: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z'
  },
  // { 
  //   name: 'GitHub', 
  //   href: 'https://github.com/devbydo',
  //   iconPath: 'M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z'
  // },
  // { 
  //   name: 'Instagram', 
  //   href: 'https://instagram.com',
  //   iconPath: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z'
  // },
]

export default function Sidebar() {
  const recaptchaEnabled = Boolean(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY)
  const [activeSection, setActiveSection] = useState('about')
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMobileScrolled, setIsMobileScrolled] = useState(false)
  const [formData, setFormData] = useState({ email: '', subject: '', message: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [returnCountdown, setReturnCountdown] = useState<number | null>(null)
  const { theme, toggleTheme } = useTheme()
  const { t } = useLocale()
  const emailInputRef = useRef<HTMLInputElement>(null)
  const mobileMenuRef = useRef<HTMLDivElement>(null)
  const mobileMenuButtonTopRef = useRef<HTMLButtonElement>(null)
  const mobileMenuButtonStickyRef = useRef<HTMLButtonElement>(null)
  const returnTimeoutRef = useRef<number | null>(null)
  const [runNameShimmer, setRunNameShimmer] = useState(false)
  const nameShimmerScheduledRef = useRef(false)
  const { isLoaderActive } = useLoader()
  const mobileFormId = useId()
  const desktopFormId = useId()
  const m = (name: 'email' | 'subject' | 'message') => `${mobileFormId}-${name}`
  const d = (name: 'email' | 'subject' | 'message') => `${desktopFormId}-${name}`

  // Run name shimmer only after loader finishes so it's visible
  useEffect(() => {
    if (nameShimmerScheduledRef.current) return
    if (isLoaderActive) return
    nameShimmerScheduledRef.current = true
    const t = window.setTimeout(() => setRunNameShimmer(true), 120)
    return () => window.clearTimeout(t)
  }, [isLoaderActive])

  useEffect(() => {
    const onScrollForStickyNav = () => {
      setIsMobileScrolled(window.scrollY > 48)
    }
    window.addEventListener('scroll', onScrollForStickyNav, { passive: true })
    onScrollForStickyNav()
    return () => window.removeEventListener('scroll', onScrollForStickyNav)
  }, [])

  // IntersectionObserver avoids layout reads (getBoundingClientRect) on each scroll.
  useEffect(() => {
    const sectionIds = [
      'about',
      'projects',
      'experience',
      'notes',
      'design-archive',
    ] as const
    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => Boolean(el))

    if (elements.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length === 0) return
        visible.sort((a, b) => b.intersectionRatio - a.intersectionRatio)
        const id = visible[0].target.id
        if (id) setActiveSection(id)
      },
      {
        root: null,
        // ~100px below top of viewport (matches prior scrollY + 100 “scanner” line), band across mid viewport
        rootMargin: '-100px 0px -45% 0px',
        threshold: [0, 0.05, 0.1, 0.2, 0.35, 0.5, 0.65, 0.8, 1],
      }
    )

    elements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  // Focus the email input when the form opens
  useEffect(() => {
    if (showEmailForm && !submitSuccess && emailInputRef.current) {
      // Delay to ensure the form is visible before focusing
      setTimeout(() => {
        emailInputRef.current?.focus()
      }, 350) // Slightly longer than the transition delay (300ms)
    }
  }, [showEmailForm, submitSuccess])

  useEffect(() => {
    return () => {
      if (returnTimeoutRef.current) {
        window.clearTimeout(returnTimeoutRef.current)
        returnTimeoutRef.current = null
      }
    }
  }, [])

  const resetEmailState = () => {
    setIsSubmitting(false)
    setSubmitError(null)
    setSubmitSuccess(false)
    setReturnCountdown(null)
    if (returnTimeoutRef.current) {
      window.clearTimeout(returnTimeoutRef.current)
      returnTimeoutRef.current = null
    }
  }

  const handleEmailButtonClick = () => {
    resetEmailState()
    setShowEmailForm(true)
  }

  const openResumePdf = () => {
    window.open('/docs/damen-oosthuizen.pdf', '_blank', 'noopener,noreferrer')
  }

  const openExternal = (href: string) => {
    window.open(href, '_blank', 'noopener,noreferrer')
  }

  const closeMobileMenuKeepForm = () => {
    setIsMobileMenuOpen(false)
  }

  const closeEmailForm = () => {
    setShowEmailForm(false)
    setFormData({ email: '', subject: '', message: '' })
    resetEmailState()
  }

  useEffect(() => {
    if (!submitSuccess) return
    if (returnCountdown === null) return

    if (returnCountdown <= 0) {
      closeEmailForm()
      return
    }

    if (returnTimeoutRef.current) {
      window.clearTimeout(returnTimeoutRef.current)
      returnTimeoutRef.current = null
    }

    returnTimeoutRef.current = window.setTimeout(() => {
      setReturnCountdown((prev) => (typeof prev === 'number' ? prev - 1 : prev))
    }, 900)

    return () => {
      if (returnTimeoutRef.current) {
        window.clearTimeout(returnTimeoutRef.current)
        returnTimeoutRef.current = null
      }
    }
  }, [submitSuccess, returnCountdown])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false)
        closeEmailForm()
      }
    }

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
      return () => {
        document.removeEventListener('keydown', handleEscape)
        document.body.style.overflow = ''
      }
    }
  }, [isMobileMenuOpen])

  useEffect(() => {
    if (!isMobileMenuOpen) {
      // After closing, return focus to the trigger (helps keyboard users).
      setTimeout(() => {
        if (isMobileScrolled) {
          mobileMenuButtonStickyRef.current?.focus()
        } else {
          mobileMenuButtonTopRef.current?.focus()
        }
      }, 0)
    }
  }, [isMobileMenuOpen, isMobileScrolled])

  const handleNavClick = () => {
    setIsMobileMenuOpen(false)
    closeEmailForm()
  }

  const handleEmailSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(false)
    setReturnCountdown(null)

    try {
      const form = e.currentTarget
      const fd = new FormData(form)
      const botField = String(fd.get('bot-field') || '')

      const recaptchaAction = 'contact'
      const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY
      let recaptchaToken: string | undefined

      if (siteKey) {
        const start = Date.now()
        while (!window.grecaptcha && Date.now() - start < 12000) {
          await new Promise((r) => setTimeout(r, 50))
        }

        if (window.grecaptcha?.ready && window.grecaptcha?.execute) {
          await new Promise<void>((resolve) => window.grecaptcha!.ready(resolve))
          recaptchaToken = await window.grecaptcha.execute(siteKey, { action: recaptchaAction })
        }

        if (!recaptchaToken) {
          throw new Error(
            'reCAPTCHA did not load. Check that your network or ad blocker allows google.com, then refresh. If you develop locally, ensure localhost is allowed for your reCAPTCHA key.',
          )
        }
      }

      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          // honeypot field (bots)
          botField,
          recaptchaToken,
          recaptchaAction,
        }),
      })

      if (!res.ok) {
        // Try to surface server-provided message if available
        const data = (await res.json().catch(() => null)) as null | {
          error?: string
          hint?: string
          code?: string
          responseCode?: number
          message?: string
        }

        const parts = [
          data?.error || `Submission failed (${res.status})`,
          data?.hint,
          data?.message,
          data?.code || data?.responseCode
            ? `(${[data?.code, data?.responseCode].filter(Boolean).join(' ')})`
            : undefined,
        ].filter(Boolean)

        const msg = parts.join(' ')

        // Local dev: only jump to mailto when the API has no SMTP (501), not on reCAPTCHA etc.
        if (process.env.NODE_ENV !== 'production' && res.status === 501) {
          const mailtoLink = `mailto:info@damenoosthuizen.com?subject=${encodeURIComponent(
            formData.subject
          )}&body=${encodeURIComponent(`From: ${formData.email}\n\n${formData.message}`)}`
          window.location.href = mailtoLink
        }

        throw new Error(msg)
      }

      setSubmitSuccess(true)
      setReturnCountdown(3)
    } catch (err) {
      const msg =
        err instanceof Error && err.message
          ? err.message
          : 'Something went wrong sending your message. Please try again.'
      setSubmitError(msg)
      setSubmitSuccess(false)
      setReturnCountdown(null)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      {showEmailForm && recaptchaEnabled ? <RecaptchaLoader /> : null}
      {/* Mobile: hamburger menu (desktop sidebar remains unchanged) */}
      <div className="lg:hidden">
        {!isMobileMenuOpen ? (
          <>
            {/* Sticky nav that appears after scrolling */}
            <div
              className={[
                'fixed top-0 left-0 right-0 z-[9997] bg-background/95 backdrop-blur transition-transform duration-300',
                isMobileScrolled ? 'translate-y-0' : '-translate-y-full',
              ].join(' ')}
              aria-hidden={isMobileScrolled ? undefined : 'true'}
            >
              <div className="relative h-14">
                {/* Hamburger appears with the sticky bar */}
                <button
                  ref={mobileMenuButtonStickyRef}
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="absolute top-4 right-4 text-foreground/60 hover:text-foreground transition-colors h-11 w-11 flex items-center justify-center focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2 rounded"
                  aria-expanded={isMobileMenuOpen}
                  aria-controls="mobile-menu"
                  aria-label={t('common.openMenu')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="w-full px-4 sm:px-6 pt-[4.5rem] pb-8">
              {/* Floating hamburger (only near the top) */}
            <button
              ref={mobileMenuButtonTopRef}
              onClick={() => setIsMobileMenuOpen(true)}
              className={[
                'fixed top-4 right-4 z-[9998] text-foreground/60 hover:text-foreground transition-all duration-300 h-11 w-11 flex items-center justify-center focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2 rounded',
                isMobileScrolled ? 'opacity-0 pointer-events-none -translate-y-2' : 'opacity-100 translate-y-0',
              ].join(' ')}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={t('common.openMenu')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>

            <div className="mt-[5px] flex items-center gap-2 flex-wrap">
              <h1 className="text-[1.35rem] min-[400px]:text-[1.5rem] min-[480px]:text-[1.65rem] leading-tight text-foreground tracking-tight font-bold font-montserrat max-[380px]:whitespace-normal whitespace-nowrap">
                <span className={`mt-[5px] ${runNameShimmer ? 'name-shimmer' : ''}`.trim()}>Damen Oosthuizen</span>
              </h1>
              <LanguageSelect placement="down" inline />
            </div>
            <h2 className="mt-1.5 text-[12px] font-semibold text-foreground/70 uppercase tracking-[0.08em]">
              <strong>{t('hero.subtitle')}</strong>
            </h2>
            <p className="mt-2 text-[11px] leading-[1.45] text-foreground/65 uppercase tracking-[0.06em]">
              {t('hero.tagline').split(' | ').join('  •  ')}
            </p>
            <p className="mt-2 text-[13px] leading-[1.45] text-foreground/75 tracking-[0.01em] max-w-[36ch]">
              {t('hero.intro')}
            </p>
            </div>
          </>
        ) : null}

        {isMobileMenuOpen ? (
          <div className="fixed inset-0 z-[9999] bg-background">
            <div
              ref={mobileMenuRef}
              id="mobile-menu"
              className="relative h-full w-full overflow-y-auto px-4 sm:px-6 pt-[4.5rem] pb-10 flex flex-col"
              role="dialog"
              aria-modal="true"
              aria-label={t('nav.mobileNavigation')}
            >
              <button
                type="button"
                onClick={() => {
                  setIsMobileMenuOpen(false)
                  closeEmailForm()
                }}
                className="absolute top-6 right-4 sm:right-6 text-foreground/60 hover:text-foreground transition-colors h-11 w-11 flex items-center justify-center focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2 rounded"
                aria-label={t('common.closeMenu')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>

              <div className="mt-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="mt-[5px] text-[1.35rem] min-[400px]:text-[1.5rem] min-[480px]:text-[1.65rem] leading-tight font-bold text-foreground tracking-tight font-montserrat max-[380px]:whitespace-normal whitespace-nowrap">
                    <span className={`mt-[5px] ${runNameShimmer ? 'name-shimmer' : ''}`.trim()}>Damen Oosthuizen</span>
                  </h2>
                  <LanguageSelect placement="down" inline />
                </div>
                <h2 className="pt-1.5 text-[12px] font-semibold text-foreground/70 uppercase tracking-[0.08em]">
                  <strong>{t('hero.subtitle')}</strong>
                </h2>
                <p className="mt-2 text-[11px] leading-[1.45] text-foreground/65 uppercase tracking-[0.06em]">
                  {t('hero.tagline').split(' | ').join('  •  ')}
                </p>
                <p className="mt-2 text-[13px] leading-[1.45] text-foreground/75 tracking-[0.01em] max-w-[36ch]">
                  {t('hero.intro')}
                </p>
              </div>

              <nav aria-label={t('nav.sectionNavigation')} className="mt-10">
                <ul className="space-y-5">
                  {navItemsKeys.map((item) => {
                    const sectionId = item.href.substring(1)
                    const isActive = activeSection === sectionId
                    return (
                      <li key={item.key}>
                        <a
                          href={item.href}
                          onClick={handleNavClick}
                          className="flex items-center justify-between focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2 rounded"
                          aria-current={isActive ? 'page' : undefined}
                        >
                          <span className="text-[14px] font-medium text-foreground">
                            {t(item.key)}
                          </span>
                          <span className="text-foreground/45" aria-hidden="true">
                            →
                          </span>
                        </a>
                      </li>
                    )
                  })}
                </ul>
              </nav>

              <div className="mt-auto border-t border-black/10 dark:border-white/10 pt-8">
                <div className="flex flex-wrap gap-2">
                  {!showEmailForm ? (
                    <AnimatedPillButton
                      onClick={handleEmailButtonClick}
                      aria-expanded={showEmailForm}
                      className="text-[13px] px-3 py-2 gap-2 whitespace-nowrap"
                      borderDrawDelayMs={0}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="shrink-0"
                        aria-hidden
                      >
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                      <span>{t('nav.emailCta')}</span>
                    </AnimatedPillButton>
                  ) : (
                    <AnimatedPillButton
                      onClick={() => closeEmailForm()}
                      aria-label={t('nav.closeEmailForm')}
                      className="text-[13px] px-3 py-2 gap-2 whitespace-nowrap"
                      borderDrawDelayMs={0}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="shrink-0"
                        aria-hidden
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      <span>{t('nav.closeEmailForm')}</span>
                    </AnimatedPillButton>
                  )}
                    <AnimatedPillButton
                      onClick={() => {
                      openResumePdf()
                      closeMobileMenuKeepForm()
                    }}
                    className="text-[13px] px-3 py-2 gap-2 whitespace-nowrap"
                    borderDrawDelayMs={280}
                  >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="shrink-0"
                        aria-hidden
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      <span className="inline-flex items-center gap-1">
                        <span>{t('sections.resume')}</span>
                        <span className="sr-only"> ({t('common.opensInNewWindow')})</span>
                      </span>
                  </AnimatedPillButton>
                  {socialLinks.map((link) => (
                    <AnimatedPillButton
                      key={link.name}
                      onClick={() => {
                        openExternal(link.href)
                        closeMobileMenuKeepForm()
                      }}
                      className="text-[13px] px-3 py-2 gap-2 whitespace-nowrap"
                      borderDrawDelayMs={560}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="shrink-0"
                        aria-hidden
                      >
                        <path d={link.iconPath} />
                      </svg>
                      <span className="inline-flex items-center gap-1">
                        <span>{t('nav.linkedin')}</span>
                        <span className="sr-only"> ({t('common.opensInNewWindow')})</span>
                      </span>
                    </AnimatedPillButton>
                  ))}
                  <AnimatedPillButton
                    onClick={toggleTheme}
                    className="text-[13px] px-3 py-2 gap-2 whitespace-nowrap"
                    borderDrawDelayMs={840}
                  >
                    {theme === 'light' ? (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="shrink-0"
                          aria-hidden
                        >
                          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                        </svg>
                        <span>{t('theme.modeDark')}</span>
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="shrink-0"
                          aria-hidden
                        >
                          <circle cx="12" cy="12" r="5" />
                          <line x1="12" y1="1" x2="12" y2="3" />
                          <line x1="12" y1="21" x2="12" y2="23" />
                          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                          <line x1="1" y1="12" x2="3" y2="12" />
                          <line x1="21" y1="12" x2="23" y2="12" />
                          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                        </svg>
                        <span>{t('theme.modeLight')}</span>
                      </>
                    )}
                  </AnimatedPillButton>
                </div>

                {showEmailForm ? (
                  <form onSubmit={handleEmailSubmit} className="mt-4 space-y-3">
                    <p className="hidden">
                      <label>
                        {t('form.botFieldLabel')}
                        <input name="bot-field" />
                      </label>
                    </p>

                    {submitSuccess ? (
                      <div className="rounded border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.03] p-4">
                        <p className="text-[15px] text-foreground/80" role="status" aria-live="polite">
                          {t('form.messageSent')}
                        </p>
                        <p className="mt-2 text-[12px] text-foreground/65">
                          {t('form.returningIn')} {returnCountdown ?? 0}.
                        </p>
                      </div>
                    ) : (
                      <>
                        <label htmlFor={m('email')} className="sr-only">
                          {t('form.yourEmail')}
                        </label>
                        <input
                          id={m('email')}
                          ref={emailInputRef}
                          type="email"
                          placeholder={t('form.yourEmail')}
                          autoComplete="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          disabled={isSubmitting}
                          className="w-full px-3 py-2 text-sm bg-background border border-black/20 dark:border-white/20 rounded text-foreground placeholder:text-foreground/50 focus:outline-none focus:border-black dark:focus:border-white transition-colors disabled:opacity-60"
                        />
                        <label htmlFor={m('subject')} className="sr-only">
                          {t('form.subject')}
                        </label>
                        <input
                          id={m('subject')}
                          type="text"
                          placeholder={t('form.subject')}
                          autoComplete="off"
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          required
                          disabled={isSubmitting}
                          className="w-full px-3 py-2 text-sm bg-background border border-black/20 dark:border-white/20 rounded text-foreground placeholder:text-foreground/50 focus:outline-none focus:border-black dark:focus:border-white transition-colors disabled:opacity-60"
                        />
                        <label htmlFor={m('message')} className="sr-only">
                          {t('form.message')}
                        </label>
                        <textarea
                          id={m('message')}
                          placeholder={t('form.message')}
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          required
                          disabled={isSubmitting}
                          rows={4}
                          className="w-full px-3 py-2 text-sm bg-background border border-black/20 dark:border-white/20 rounded text-foreground placeholder:text-foreground/50 focus:outline-none focus:border-black dark:focus:border-white transition-colors resize-none disabled:opacity-60"
                        />
                        {submitError ? (
                          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                            {submitError}
                          </p>
                        ) : null}
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full px-3 py-2 text-sm font-medium text-white dark:text-black bg-black dark:bg-white hover:bg-black/80 dark:hover:bg-white/80 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? t('form.sending') : t('form.send')}
                        </button>
                        {recaptchaEnabled ? (
                          <p className="pt-2 text-[11px] text-foreground/55 leading-relaxed">
                            {t('form.recaptchaProtected')}{' '}
                            <a
                              href="https://policies.google.com/privacy"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline hover:no-underline"
                            >
                              {t('form.privacy')}
                            </a>{' '}
                            ·{' '}
                            <a
                              href="https://policies.google.com/terms"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline hover:no-underline"
                            >
                              {t('form.terms')}
                            </a>
                          </p>
                        ) : null}
                      </>
                    )}
                  </form>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Desktop: existing sidebar */}
      <aside className="hidden lg:sticky lg:top-0 lg:flex lg:max-h-screen lg:min-h-0 lg:overflow-y-auto lg:w-[48%] lg:flex-col lg:justify-between lg:pt-24 lg:pb-8 w-full px-4 sm:px-6 md:px-8 lg:px-12 xl:px-[3em] py-8 md:py-12 flex-col justify-between bg-background">
        <div>
          <div className="mb-10">
            <div className="mt-[5px] flex items-center gap-2 flex-wrap mb-2">
              <h1 className="text-[1.5rem] leading-none text-foreground tracking-tight font-bold uppercase font-montserrat whitespace-nowrap">
                <span className={`mt-[5px] ${runNameShimmer ? 'name-shimmer' : ''}`.trim()}>Damen Oosthuizen</span>
              </h1>
              <LanguageSelect placement="down" inline />
            </div>
            <h2 className="text-sm md:text-base font-normal text-neutral-600 dark:text-neutral-400 mb-2 uppercase">
              {t('hero.subtitle')}
            </h2>
            <p className="mt-4 mb-6 text-[12px] leading-[1.5] text-foreground/65 uppercase tracking-[0.08em] max-w-[42ch]">
              {t('hero.tagline').split(' | ').join('  •  ')}
            </p>
            <div className={isLoaderActive ? 'opacity-0' : undefined}>
              <div className="border-l-2 border-neutral-200 dark:border-neutral-600 pl-4 py-1 mb-8 max-w-[38ch]">
                <p className="text-[15px] leading-[1.65] text-foreground/75">
                  {t('hero.intro')}
                  <br /><br />
                  <span className="italic text-foreground/75">
                    <span className="font-semibold not-italic">
                      {t('hero.flexibleByDesign').split(' — ')[0]}
                    </span>
                    {' — '}
                    {t('hero.flexibleByDesign').split(' — ').slice(1).join(' — ')}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <nav className="relative mt-2" aria-label={t('nav.sectionNavigation')}>
              <ul 
                className={`space-y-0 transition-all duration-500 ease-in-out ${
                  showEmailForm 
                    ? 'opacity-0 -translate-x-8 pointer-events-none' 
                    : 'opacity-100 translate-x-0 pointer-events-auto'
                }`}
              >
                {navItemsKeys.map((item) => {
                  const sectionId = item.href.substring(1)
                  const isActive = activeSection === sectionId
                  return (
                    <li key={item.key}>
                      <a
                        href={item.href}
                        className={`group inline-flex items-center leading-4 font-medium text-foreground transition-all duration-300 ease-in-out min-h-[44px] focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2 rounded ${
                          isActive ? 'text-[14px] opacity-100' : 'text-[14px] opacity-60 group-hover:opacity-100'
                        }`}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        <span
                          className={`h-px bg-black/60 dark:bg-white/60 mr-3 transition-all duration-300 ease-in-out ${
                            isActive
                              ? 'w-16 opacity-70'
                              : 'w-8 opacity-20 group-hover:w-16 group-hover:opacity-50'
                          }`}
                        />
                        {t(item.key).toUpperCase()}
                      </a>
                    </li>
                  )
                })}
              </ul>

              {/* Email form header - always visible when form is active */}
              <div
                className={`absolute top-0 left-0 w-full transition-all duration-500 ease-in-out ${
                  showEmailForm 
                    ? 'opacity-100 translate-x-0 pointer-events-auto' 
                    : 'opacity-0 translate-x-8 pointer-events-none'
                }`}
              >
                <div className="inline-flex items-center w-full">
                  <button
                    onClick={() => {
                      closeEmailForm()
                    }}
                    className="group inline-flex items-center leading-4 font-medium text-foreground transition-all duration-300 ease-in-out min-h-[44px] focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2 rounded text-[14px] opacity-100"
                    aria-expanded={showEmailForm}
                  >
                    <span className="h-px bg-black/60 dark:bg-white/60 mr-3 transition-all duration-300 ease-in-out w-16 opacity-70" />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-2"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    {t('nav.emailMe').toUpperCase()}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      closeEmailForm()
                    }}
                    className="ml-auto text-foreground/60 hover:text-foreground transition-colors min-h-[32px] min-w-[32px] flex items-center justify-center focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2 rounded"
                    aria-label={t('nav.closeEmailForm')}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>

                     {/* Email form */}
              <form
                onSubmit={handleEmailSubmit}
                className={`mt-3 space-y-3 pb-4 transition-all duration-500 ease-out ${
                  showEmailForm 
                    ? 'opacity-100 translate-y-0 pointer-events-auto delay-300' 
                    : 'opacity-0 translate-y-6 pointer-events-none delay-0'
                }`}
              >
                <p className="hidden">
                  <label>
                    {t('form.botFieldLabel')}
                    <input name="bot-field" />
                  </label>
                </p>
                {submitSuccess ? (
                  <div className="rounded border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.03] p-4">
                    <p className="text-[15px] text-foreground/80" role="status" aria-live="polite">
                      {t('form.messageSent')}
                    </p>
                    <p className="mt-2 text-[12px] text-foreground/65">
                      {t('form.returningIn')} {returnCountdown ?? 0}.
                    </p>
                  </div>
                ) : (
                  <>
                    <label htmlFor={d('email')} className="sr-only">
                      {t('form.yourEmail')}
                    </label>
                    <input
                      id={d('email')}
                      ref={emailInputRef}
                      type="email"
                      placeholder={t('form.yourEmail')}
                      autoComplete="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 text-sm bg-background border border-black/20 dark:border-white/20 rounded text-foreground placeholder:text-foreground/50 focus:outline-none focus:border-black dark:focus:border-white transition-colors disabled:opacity-60"
                    />
                    <label htmlFor={d('subject')} className="sr-only">
                      {t('form.subject')}
                    </label>
                    <input
                      id={d('subject')}
                      type="text"
                      placeholder={t('form.subject')}
                      autoComplete="off"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 text-sm bg-background border border-black/20 dark:border-white/20 rounded text-foreground placeholder:text-foreground/50 focus:outline-none focus:border-black dark:focus:border-white transition-colors disabled:opacity-60"
                    />
                    <label htmlFor={d('message')} className="sr-only">
                      {t('form.message')}
                    </label>
                    <textarea
                      id={d('message')}
                      placeholder={t('form.message')}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      disabled={isSubmitting}
                      rows={3}
                      className="w-full px-3 py-2 text-sm bg-background border border-black/20 dark:border-white/20 rounded text-foreground placeholder:text-foreground/50 focus:outline-none focus:border-black dark:focus:border-white transition-colors resize-none disabled:opacity-60"
                    />
                    {submitError ? (
                      <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                        {submitError}
                      </p>
                    ) : null}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 text-sm font-medium text-white dark:text-black bg-black dark:bg-white hover:bg-black/80 dark:hover:bg-white/80 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? t('form.sending') : t('form.send')}
                    </button>
                    {recaptchaEnabled ? (
                      <p className="pt-2 text-[11px] text-foreground/55 leading-relaxed">
                        {t('form.recaptchaProtected')}{' '}
                        <a
                          href="https://policies.google.com/privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:no-underline"
                        >
                          {t('form.privacy')}
                        </a>{' '}
                        ·{' '}
                        <a
                          href="https://policies.google.com/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:no-underline"
                        >
                          {t('form.terms')}
                        </a>
                      </p>
                    ) : null}
                  </>
                )}
              </form>
              </div>

             
            </nav>
      </div>
      <div className="pt-6 -mx-1 px-1 min-w-0">
        <div className="overflow-x-auto overflow-y-visible scrollbar-hide py-2">
          <ul className="flex flex-nowrap items-center gap-2.5">
          {!showEmailForm ? (
            <li className="flex-none">
              <AnimatedPillButton
                onClick={handleEmailButtonClick}
                aria-expanded={showEmailForm}
                className="text-[14px] px-4 py-2.5 gap-2 whitespace-nowrap"
                borderDrawDelayMs={0}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0"
                  aria-hidden
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <span>{t('nav.emailCta')}</span>
              </AnimatedPillButton>
            </li>
          ) : null}
          <li className="flex-none">
            <AnimatedPillButton
              onClick={openResumePdf}
              className="text-[14px] px-4 py-2.5 gap-2 whitespace-nowrap"
              borderDrawDelayMs={280}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
                aria-hidden
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span className="inline-flex items-center gap-1">
                <span>{t('sections.resume')}</span>
                <span className="sr-only"> ({t('common.opensInNewWindow')})</span>
              </span>
            </AnimatedPillButton>
          </li>
          {socialLinks.map((link) => (
            <li key={link.name} className="flex-none">
              <AnimatedPillButton
                onClick={() => openExternal(link.href)}
                className="text-[14px] px-4 py-2.5 gap-2 whitespace-nowrap"
                borderDrawDelayMs={560}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="shrink-0"
                  aria-hidden
                >
                  <path d={link.iconPath} />
                </svg>
                <span className="inline-flex items-center gap-1">
                  <span>{t('nav.linkedin')}</span>
                  <span className="sr-only"> ({t('common.opensInNewWindow')})</span>
                </span>
              </AnimatedPillButton>
            </li>
          ))}
          <li className="flex-none">
            <AnimatedPillButton
              onClick={toggleTheme}
              className="text-[14px] px-4 py-2.5 gap-2 whitespace-nowrap"
              borderDrawDelayMs={840}
            >
              {theme === 'light' ? (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="shrink-0"
                    aria-hidden
                  >
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                  </svg>
                  <span>{t('theme.modeDark')}</span>
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="shrink-0"
                    aria-hidden
                  >
                    <circle cx="12" cy="12" r="5" />
                    <line x1="12" y1="1" x2="12" y2="3" />
                    <line x1="12" y1="21" x2="12" y2="23" />
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                    <line x1="1" y1="12" x2="3" y2="12" />
                    <line x1="21" y1="12" x2="23" y2="12" />
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                  </svg>
                  <span>{t('theme.modeLight')}</span>
                </>
              )}
            </AnimatedPillButton>
          </li>
          </ul>
        </div>
      </div>
    </aside>
    </>
  )
}
